// 微电网拓扑 GLB 小图标（卡片右侧）：仅 three + R3F，不引入 drei/mediapipe（避免 source map 告警）
import React, { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useLoader, useThree } from '@react-three/fiber';
import { AnimationMixer, Box3, LoopRepeat, Vector3 } from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

const STATES = new Set(['normal', 'fault', 'offline']);
const FIT_MARGIN = 1.2;

export function topoGlbUrl(device, state = 'normal') {
  const st = STATES.has(state) ? state : 'normal';
  return `${process.env.PUBLIC_URL || ''}/assets/topo-glb/topo_${device}_${st}.glb`;
}

/** SVG viewBox 坐标 → 覆盖层百分比定位（卡片外框尺寸保持与原 rect 一致） */
export function ef2Pct(x, y, w, h, vbW = 900, vbH = 300) {
  return {
    left: `${(x / vbW) * 100}%`,
    top: `${(y / vbH) * 100}%`,
    width: `${(w / vbW) * 100}%`,
    height: `${(h / vbH) * 100}%`,
  };
}

function Model({ url, animate }) {
  const root = useRef();
  const gltf = useLoader(GLTFLoader, url);
  const cloned = useMemo(() => gltf.scene.clone(true), [gltf.scene]);
  const mixer = useMemo(() => new AnimationMixer(cloned), [cloned]);
  const { camera } = useThree();

  useEffect(() => {
    const clips = gltf.animations || [];
    const actions = clips.map((clip) => {
      const a = mixer.clipAction(clip, cloned);
      a.setLoop(LoopRepeat, Infinity);
      a.reset();
      if (animate) a.play();
      else a.stop();
      return a;
    });
    return () => {
      actions.forEach((a) => a.stop());
      mixer.stopAllAction();
    };
  }, [mixer, cloned, gltf.animations, animate]);

  useFrame((_, dt) => {
    if (animate) mixer.update(dt);
  });

  // 按包围盒对焦相机，效果近似 drei Bounds fit
  useLayoutEffect(() => {
    if (!root.current) return;
    const box = new Box3().setFromObject(root.current);
    const size = box.getSize(new Vector3());
    const center = box.getCenter(new Vector3());
    const maxSize = Math.max(size.x, size.y, size.z, 0.001);
    const fitH = maxSize / (2 * Math.tan((camera.fov * Math.PI) / 360));
    const fitW = fitH / Math.max(camera.aspect || 1, 0.01);
    const distance = FIT_MARGIN * Math.max(fitH, fitW);
    const dir = new Vector3(1, 0.75, 1).normalize();
    camera.position.copy(center).addScaledVector(dir, distance);
    camera.near = distance / 100;
    camera.far = distance * 100;
    camera.lookAt(center);
    camera.updateProjectionMatrix();
  }, [cloned, camera]);

  return (
    <group ref={root}>
      <primitive object={cloned} />
    </group>
  );
}

/** device: grid|pv|storage|load|charger|bus|diesel|wind；state: normal|fault|offline */
export default function TopoGlbIcon({ device, state = 'normal', play, className = '' }) {
  const url = useMemo(() => topoGlbUrl(device, state), [device, state]);
  const animate = play !== undefined ? play : state !== 'offline';
  const [visible, setVisible] = useState(false);

  // 延后挂载 Canvas，避免同屏多卡同时抢 WebGL 上下文
  useEffect(() => {
    if (!device) return undefined;
    const t = window.setTimeout(() => setVisible(true), 0);
    return () => window.clearTimeout(t);
  }, [device]);

  if (!device) return <div className={`ef2-glb ef2-glb--empty ${className}`.trim()} aria-hidden />;

  return (
    <div className={`ef2-glb ${className}`.trim()} aria-hidden>
      {visible ? (
        <Canvas
          camera={{ position: [2.2, 1.6, 2.2], fov: 35, near: 0.1, far: 100 }}
          dpr={[1, 1.5]}
          gl={{ alpha: true, antialias: true, powerPreference: 'low-power' }}
          style={{ width: '100%', height: '100%', display: 'block', background: 'transparent' }}
        >
          <ambientLight intensity={0.95} />
          <directionalLight position={[2.5, 4, 2]} intensity={1.2} />
          <directionalLight position={[-2, 1, -1]} intensity={0.35} />
          <Suspense fallback={null}>
            <Model url={url} animate={animate} />
          </Suspense>
        </Canvas>
      ) : null}
    </div>
  );
}

/** 卡片内统一文案区 + 右侧 GLB（变压器传 device=null 留空） */
export function Ef2NodeCard({
  name, value, unit, sub, valueColor, device = null, state = 'normal', play,
  style, className = '',
}) {
  return (
    <div className={`ef2-card ${className}`.trim()} style={style}>
      <div className="ef2-card-text">
        <div className="ef2-card-name">{name}</div>
        {value != null && value !== '' ? (
          <div className="ef2-card-val" style={valueColor ? { color: valueColor } : undefined}>
            {value}
            {unit ? <span className="ef2-card-unit">{unit}</span> : null}
          </div>
        ) : null}
        {sub ? <div className="ef2-card-sub">{sub}</div> : null}
      </div>
      <TopoGlbIcon device={device} state={state} play={play} />
    </div>
  );
}
